<template>
  <div>
    <div class="flex flex-col md:flex-row">
      <RevCard
        :class="[
          'mr-[0.1rem] flex items-start rounded-b-none px-16 pt-24 md:rounded-b-lg md:grow md:rounded-r-none md:p-32',
          { 'rounded-b-none md:rounded-b-none': isPanelOpen },
        ]"
      >
        <RevIllustration
          v-if="product.image"
          alt=""
          class="hidden md:mr-[1.6rem] md:block"
          :height="48"
          :src="product.image"
          :width="48"
        />
        <div class="mb-24 flex flex-col gap-16 overflow-hidden md:mb-0">
          <RevLink
            class="text-static-default-mid body-1-bold no-underline md:underline"
            :to="quotationsLink"
          >
            {{ product.title }}
          </RevLink>
          <div class="flex flex-col gap-16 md:flex-row md:gap-24">
            <div class="flex">
              <p class="text-static-default-mid body-2 mr-12">
                {{ i18n(translations.marketLabel) }}
              </p>
              <div class="flex gap-8 px-8 md:items-baseline">
                <Flag :country-code="quote.marketCode as MarketCountryCode" />
                <p class="text-static-default-mid body-2-bold">
                  {{ quote.marketCode }}
                </p>
              </div>
            </div>
            <div
              class="flex flex-col items-baseline justify-between gap-y-8 md:flex-row md:justify-normal md:gap-24"
            >
              <p class="text-static-default-mid body-2 flex-[2] md:mt-0">
                {{ i18n(translations.idLabel) }}
                <span>{{ product.productId }}</span>
              </p>
              <RevButtonBase
                v-if="showTurnAllOffline"
                class="text-static-default-mid body-2 cursor-pointer bg-transparent underline md:mt-0"
                @click="handleTurnAllOffline"
              >
                {{ i18n(translations.turnOffline) }}
              </RevButtonBase>
            </div>
          </div>
          <div class="flex flex-col gap-12 md:flex-row md:flex-wrap">
            <template v-for="group in listingsGroups" :key="group.title">
              <div
                v-if="group.listings.length > 0"
                class="flex shrink grow basis-0 gap-12"
              >
                <p class="text-static-default-low body-2 md:text-nowrap">
                  {{ group.title }}:
                </p>
                <div class="flex flex-col gap-12 md:flex-row md:flex-wrap">
                  <RevTag
                    v-for="listing in group.listings"
                    :key="generateListingKey(listing)"
                    class="self-start"
                    :label="getQuoteGradeName(listing.grade)"
                    :variant="getTagVariant(group.id)"
                  />
                </div>
              </div>
            </template>
          </div>
        </div>
      </RevCard>
      <RevCard
        :class="[
          'border-static-default-mid flex rounded-t-none border-t-2 py-20 md:rounded-tr-lg md:hover:bg-float-default-low-hover sm:px-24 md:rounded-l-none md:border-t-0 md:p-0 md:px-16',
          { 'rounded-b-none md:rounded-b-none': isPanelOpen },
        ]"
      >
        <RevButtonBase
          class="flex w-full cursor-pointer items-center justify-end bg-transparent md:justify-center md:px-8"
          data-test="offer-dropdown-cta"
          @click="toggleProductDetails"
        >
          <p class="text-static-default-mid body-1 grow text-left md:hidden">
            {{ i18n(translations.seeDetails) }}
          </p>
          <IconChevronDown :class="{ 'rotate-180': isPanelOpen }" />
        </RevButtonBase>
      </RevCard>
    </div>
    <Transition
      appear
      enter-active-class="transition-opacity delay-100 duration-100 ease-out"
      enter-from-class="opacity-0"
      leave-active-class="transition-opacity duration-100 ease-out"
      leave-to-class="opacity-0"
    >
      <RevCard
        v-if="isPanelOpen"
        class="flex flex-col rounded-t-none p-[2rem] md:p-0"
      >
        <template
          v-for="group in listingsGroups"
          :key="`${group.title}-${group.listings.length}`"
        >
          <div v-if="group.listings.length > 0">
            <p class="text-static-default-hi body-1-bold mb-16 mt-32 md:ml-24">
              {{ group.title }}
            </p>
            <!-- Header with columns labels -->
            <div
              class="border-static-default-low relative hidden min-h-56 items-center border-b [&>p]:body-1-bold md:grid"
              :class="gridClasses"
            >
              <p>
                {{ i18n(listingLineTranslations.listingGradeLabel) }}
              </p>
              <div class="body-1-bold flex items-center">
                <p>
                  {{ i18n(listingLineTranslations.priceToWinBackboxTitle) }}
                </p>
                <RevTooltip position="bottom-end">
                  <template #trigger="{ show, hide }">
                    <RevButtonIcon
                      class="cursor-pointer"
                      :disabled="false"
                      :icon="IconInfo"
                      variant="secondary"
                      @focusin="show"
                      @focusout="hide"
                      @mouseleave="hide"
                      @mouseover="show"
                    />
                  </template>
                  {{ i18n(listingLineTranslations.priceToWinBackboxTooltip) }}
                </RevTooltip>
              </div>
              <p>
                {{ i18n(listingLineTranslations.listingBackBoxPriceLabel) }}
              </p>
              <p>{{ i18n(listingLineTranslations.listingPriceLabel) }}</p>
              <p>{{ i18n(listingLineTranslations.listingStatusLabel) }}</p>
            </div>
            <!-- Table lines -->
            <ListingLine
              v-for="(listing, index) in group.listings"
              :key="generateListingKey(listing)"
              :grid-classes
              :index
              :is-loading="loadingGrades.includes(listing.grade)"
              :is-price-to-win-loading="
                isPriceToWinLoading &&
                listing.state === LISTING_PUBLICATION_STATE.OFFLINE
              "
              :listing
              :market-code="quote.marketCode"
              :pricing-estimation="
                getPricingEstimationByGrade(pricingEstimations, listing.grade)
              "
              :product-id="product.productId"
              :tag-variant="getTagVariant(group.id)"
              @save-price="handleUpdateListing"
              @toggle-state="handleUpdateListing"
            />
          </div>
        </template>
      </RevCard>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import type { HttpApiError, MarketCountryCode } from '@backmarket/http-api'
import { getBuybackQuote } from '@backmarket/http-api/src/api-specs-buyback/seller/buyback-quotes'
import toastTranslations from '@backmarket/nuxt-layer-buyback/utils/Toast.translations'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { sleep } from '@backmarket/utils/async/sleep'
import { tw } from '@backmarket/utils/string/tw'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevCard } from '@ds/components/Card'
import { RevIllustration } from '@ds/components/Illustration'
import { RevLink } from '@ds/components/Link'
import { RevTag, type Variants } from '@ds/components/Tag'
import { VariantsEnum } from '@ds/components/Tag/Tag.constant'
import { RevTooltip } from '@ds/components/Tooltip'
import { IconChevronDown } from '@ds/icons/IconChevronDown'
import { IconInfo } from '@ds/icons/IconInfo'

import {
  type BuyBackQuote,
  BuyBackQuoteListingStates,
  type BuyBackQuotePricingEstimation,
  LISTING_PUBLICATION_STATE,
  type PostBuyBackQuoteListing,
} from '~/api/quotes/types'
import { ROUTE_NAMES } from '~/constants/routes'
import { useGetPricingEstimations } from '~/scopes/buyback/composables/useGetPricingEstimation'
import {
  BUYBACK_QUOTES_FETCH_INTERVAL,
  BUYBACK_QUOTES_FETCH_TIMEOUT,
} from '~/scopes/buyback/constants'
import listingLineTranslations from '~/scopes/buyback/pages/wishlist/components/OfferDetails/components/ListingLine/ListingLine.translations'
import { useUpdateListings } from '~/scopes/buyback/pages/wishlist/composables/useUpdateListings'
import {
  getPricingEstimationByGrade,
  matchPostAndGetListings,
} from '~/scopes/buyback/utils/buyback.utils'

import {
  type BuyBackListingsGroup,
  GROUP_IDS,
  type GroupIds,
} from '../../WishList.types'
import { generateListingKey, getQuoteGradeName } from '../../WishList.utils'

import { groupListingsByStatus } from './OfferDetails.adapter'
import translations from './OfferDetails.translations'
import ListingLine from './components/ListingLine/ListingLine.vue'

const { closeAllToasts, openErrorToast, openSuccessToast } = useTheToast()

const props = defineProps<{ quote: BuyBackQuote }>()
const emit = defineEmits<{
  (event: 'update-timeout'): void
}>()

const i18n = useI18n()
const logger = useLogger()

const quote = ref<BuyBackQuote>(props.quote)
const loadingGrades = ref<string[]>([])
const isPanelOpen = ref(false)
const isPriceToWinLoading = ref(false)
const gridClasses = tw`
  gap-x-16 md:pl-24 md:pr-16
  md:grid-cols-[minmax(auto,142px)_1fr_1fr_1fr_1.5fr_1.75fr]
  lg:grid-cols-[minmax(auto,212px)_1.5fr_1fr_1fr_1.5fr_1.75fr]
`

const listingsGroups = ref<BuyBackListingsGroup[]>([])
const pricingEstimations = ref<BuyBackQuotePricingEstimation[]>([])
listingsGroups.value = groupListingsByStatus(quote.value.listings)

const product = computed(() => quote.value.product)

const showTurnAllOffline = computed(() => {
  const onlineGroups = listingsGroups.value.filter((group) => {
    const groupIsOnline =
      group.id === GROUP_IDS.WITH_BACKBOX ||
      group.id === GROUP_IDS.WITHOUT_BACKBOX
    const groupHasListings = group.listings.length > 0

    return groupIsOnline && groupHasListings
  })

  return onlineGroups.length > 0
})

const quotationsLink = computed(() => ({
  name: ROUTE_NAMES.BUYBACK.CREATE_QUOTATION,
  params: {
    productId: product.value.productId,
  },
  query: { countryCode: quote.value.marketCode },
}))

async function getQuote(): Promise<BuyBackQuote | null> {
  try {
    return await $httpFetch(getBuybackQuote, {
      pathParams: { productId: product.value.productId },
      queryParams: { marketCode: quote.value.marketCode },
    })
  } catch (err) {
    openErrorToast({
      title: i18n(toastTranslations.toastErrorTitle),
      content: i18n(toastTranslations.toastErrorContent),
    })

    logger.error('[BUYBACK][API][QUOTES] Error getting quote', {
      error: err as HttpApiError,
      owners: ['bot-squad-circularity-order-lifecycle-front'],
    })

    return null
  }
}

async function updatePricingEstimations() {
  const offlineListings = quote.value.listings.filter(
    (listing) => listing.state === LISTING_PUBLICATION_STATE.OFFLINE,
  )

  if (offlineListings.length === 0) {
    return
  }

  const offlineGrades = offlineListings.map((listing) => listing.grade)

  isPriceToWinLoading.value = true
  pricingEstimations.value = await useGetPricingEstimations(
    offlineGrades,
    product.value.productId,
    quote.value.marketCode,
  )

  isPriceToWinLoading.value = false
}

async function toggleProductDetails() {
  if (isPanelOpen.value) {
    isPanelOpen.value = false

    return
  }

  isPanelOpen.value = true
  await updatePricingEstimations()
}

function resetValues(newQuote: BuyBackQuote) {
  quote.value = newQuote
  listingsGroups.value = groupListingsByStatus(newQuote.listings)
  loadingGrades.value = []
}

let startTime: number | null = null
async function pollUpdatedListings(
  listingsPostPayload: PostBuyBackQuoteListing[],
) {
  if (!startTime) {
    startTime = Date.now()
  }

  const upToDateQuote = await getQuote()

  if (upToDateQuote === null) {
    return
  }

  if (Date.now() - startTime > BUYBACK_QUOTES_FETCH_TIMEOUT) {
    startTime = null
    closeAllToasts()
    emit('update-timeout')
    resetValues(upToDateQuote)
    await updatePricingEstimations()

    return
  }

  if (!matchPostAndGetListings(listingsPostPayload, upToDateQuote.listings)) {
    await sleep(BUYBACK_QUOTES_FETCH_INTERVAL)
    await pollUpdatedListings(listingsPostPayload)

    return
  }

  startTime = null
  resetValues(upToDateQuote)
  openSuccessToast()
  await updatePricingEstimations()
}

async function saveListings(listings: PostBuyBackQuoteListing[]) {
  loadingGrades.value = listings.map((l) => l.grade)

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const { product, marketCode } = quote.value
  const { shouldFetch } = await useUpdateListings({
    productId: product.productId,
    marketCode,
    listings,
  })

  if (shouldFetch) {
    await pollUpdatedListings(listings)
  }
}

function handleTurnAllOffline() {
  const onlineListings = quote.value.listings.filter(
    (listing) => listing.state === LISTING_PUBLICATION_STATE.ONLINE,
  )

  const listings = onlineListings.map((listing) => {
    return {
      grade: listing.grade,
      state: BuyBackQuoteListingStates.DISABLED,
    }
  })

  saveListings(listings)
}

function handleUpdateListing(listing: PostBuyBackQuoteListing) {
  saveListings([listing])
}

function getTagVariant(groupId: GroupIds): Variants {
  const tagVariantMap: Record<GroupIds, VariantsEnum> = {
    [GROUP_IDS.WITHOUT_BACKBOX]: VariantsEnum.Danger,
    [GROUP_IDS.WITH_BACKBOX]: VariantsEnum.Success,
    [GROUP_IDS.OFFLINE]: VariantsEnum.Secondary,
  }

  return tagVariantMap[groupId]
}
</script>
